/**
 * ReportEditor — drag-and-drop grid with widget palette + per-widget
 * config. The editor mutates a local ReportSpec draft; the parent commits
 * via PUT /api/reports/:id on save. Add-widget buttons insert a minimally-
 * valid widget of the selected type at the first free y-coordinate.
 *
 * The editor intentionally does NOT re-validate the spec client-side —
 * the backend Zod schema is the single source of truth and rejects bad
 * shapes at PUT. This keeps the frontend bundle lean and prevents two
 * slightly-different validation layers from drifting.
 */

import { useMemo, useState } from 'react'
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout'
import { BarChart3, LineChart, Table2, Gauge, Columns, Trash2 } from 'lucide-react'

import type {
  MetricName,
  ReportSpec,
  WidgetSpec,
} from './types'
import { METRIC_NAMES } from './types'
import { WidgetRenderer } from './WidgetRenderer'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = WidthProvider(Responsive)

interface ReportEditorProps {
  value: ReportSpec
  onChange: (spec: ReportSpec) => void
}

const DEFAULT_METRIC: MetricName = 'revenue_cents'

function nextY(widgets: WidgetSpec[]): number {
  if (widgets.length === 0) return 0
  return Math.max(...widgets.map((w) => w.layout.y + w.layout.h))
}

function makeWidget(type: WidgetSpec['type'], widgets: WidgetSpec[]): WidgetSpec {
  const y = nextY(widgets)
  const id = `${type}_${Date.now().toString(36)}`
  const base = { id, title: `New ${type}`, layout: { x: 0, y, w: 6, h: 3 } }
  switch (type) {
    case 'kpi':
      return { ...base, type, metric: DEFAULT_METRIC, layout: { x: 0, y, w: 3, h: 2 } }
    case 'bar':
      return { ...base, type, metric: DEFAULT_METRIC, group_by: 'day', limit: 10 }
    case 'line':
      return { ...base, type, metric: DEFAULT_METRIC, grain: 'day', overlay: [] }
    case 'table':
      return {
        ...base,
        type,
        metric: DEFAULT_METRIC,
        columns: ['bucket', DEFAULT_METRIC],
        limit: 20,
      }
    case 'comparison':
      return { ...base, type, metrics: [DEFAULT_METRIC], layout: { x: 0, y, w: 6, h: 4 } }
    default:
      return { ...base, type: 'kpi', metric: DEFAULT_METRIC, layout: { x: 0, y, w: 3, h: 2 } }
  }
}

const PALETTE: Array<{ type: WidgetSpec['type']; label: string; Icon: typeof Gauge }> = [
  { type: 'kpi', label: 'KPI', Icon: Gauge },
  { type: 'bar', label: 'Bar', Icon: BarChart3 },
  { type: 'line', label: 'Line', Icon: LineChart },
  { type: 'table', label: 'Table', Icon: Table2 },
  { type: 'comparison', label: 'Compare', Icon: Columns },
]

export function ReportEditor({ value, onChange }: ReportEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const layouts = useMemo<{ lg: Layout[] }>(() => {
    return {
      lg: value.widgets.map((w) => ({
        i: w.id,
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
      })),
    }
  }, [value.widgets])

  function onLayoutChange(newLayout: Layout[]) {
    const map = new Map(newLayout.map((l) => [l.i, l]))
    const next = value.widgets.map((w) => {
      const l = map.get(w.id)
      if (!l) return w
      return { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } }
    })
    onChange({ ...value, widgets: next })
  }

  function addWidget(type: WidgetSpec['type']) {
    const w = makeWidget(type, value.widgets)
    onChange({ ...value, widgets: [...value.widgets, w] })
    setSelectedId(w.id)
  }

  function removeWidget(id: string) {
    onChange({ ...value, widgets: value.widgets.filter((w) => w.id !== id) })
    if (selectedId === id) setSelectedId(null)
  }

  function updateWidget(id: string, patch: Partial<WidgetSpec>) {
    onChange({
      ...value,
      widgets: value.widgets.map((w) => (w.id === id ? ({ ...w, ...patch } as WidgetSpec) : w)),
    })
  }

  const selected = value.widgets.find((w) => w.id === selectedId) ?? null

  return (
    <div className="flex min-h-[500px] gap-4">
      {/* Palette */}
      <aside className="flex w-48 flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Widget palette
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PALETTE.map(({ type, label, Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => addWidget(type)}
              className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 hover:border-butler-500 hover:bg-butler-50 hover:text-butler-700 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-200"
              data-testid={`palette-${type}`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {selected && (
          <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-700">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Configure
            </div>
            <label className="mt-2 block text-[11px] text-slate-500">
              Title
              <input
                className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={selected.title}
                onChange={(e) => updateWidget(selected.id, { title: e.target.value })}
              />
            </label>
            {'metric' in selected && (
              <label className="mt-2 block text-[11px] text-slate-500">
                Metric
                <select
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  value={selected.metric}
                  onChange={(e) =>
                    updateWidget(selected.id, { metric: e.target.value as MetricName })
                  }
                >
                  {METRIC_NAMES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={() => removeWidget(selected.id)}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-rose-50 px-2 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-200"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove widget
            </button>
          </div>
        )}
      </aside>

      {/* Grid */}
      <div className="flex-1" data-testid="report-editor">
        {value.widgets.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-800">
            No widgets yet. Pick a type from the palette to start.
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1024, md: 768, sm: 0 }}
            cols={{ lg: 12, md: 10, sm: 6 }}
            rowHeight={60}
            onLayoutChange={onLayoutChange}
            margin={[12, 12]}
          >
            {value.widgets.map((w) => (
              <div
                key={w.id}
                onClickCapture={() => setSelectedId(w.id)}
                className={`${selectedId === w.id ? 'ring-2 ring-butler-500' : ''} rounded-xl`}
              >
                <WidgetRenderer widget={w} />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </div>
    </div>
  )
}
